import React from 'react';
import { Mui, MuiIcons } from '@osu/react-ui';
import usePullToRefresh from '../../hooks/usePullToRefresh';

/**
 * PullToRefresh component provides pull-to-refresh functionality for mobile
 */
function PullToRefresh({ 
  children, 
  onRefresh, 
  enabled = true, 
  className = '' 
}) {
  const pullToRefresh = usePullToRefresh({
    onRefresh,
    enabled: enabled && window.innerWidth < 768, // Only enable on mobile
    threshold: 80,
    resistance: 2.5
  });

  const getRefreshIcon = () => {
    switch (pullToRefresh.pullState) {
      case 'refreshing':
        return <MuiIcons.Refresh className="refresh-icon spinning" />;
      case 'ready':
        return <MuiIcons.Refresh className="refresh-icon ready" />;
      case 'pulling':
        return <MuiIcons.ArrowDownward className="refresh-icon pulling" />;
      default:
        return <MuiIcons.Refresh className="refresh-icon idle" />;
    }
  };

  const getRefreshMessage = () => {
    switch (pullToRefresh.pullState) {
      case 'refreshing':
        return 'Refreshing...';
      case 'ready':
        return 'Release to refresh';
      case 'pulling':
        return 'Pull to refresh';
      default:
        return '';
    }
  };

  return (
    <div 
      ref={pullToRefresh.ref}
      className={`pull-to-refresh-container ${className}`}
      style={{
        position: 'relative',
        overflow: 'auto',
        height: '100%'
      }}
    >
      {/* Pull indicator */}
      {(pullToRefresh.isPulling || pullToRefresh.isRefreshing) && (
        <div 
          className="pull-indicator"
          style={{
            position: 'absolute',
            top: Math.max(-60, -60 + pullToRefresh.pullDistance),
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '10px',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '20px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
            transition: pullToRefresh.isRefreshing ? 'top 0.3s ease' : 'none',
            opacity: pullToRefresh.pullProgress
          }}
        >
          <div 
            style={{
              transform: `rotate(${pullToRefresh.pullProgress * 180}deg)`,
              transition: pullToRefresh.pullState === 'refreshing' ? 'none' : 'transform 0.2s ease'
            }}
          >
            {getRefreshIcon()}
          </div>
          <Mui.Typography 
            variant="caption" 
            style={{ 
              marginTop: '4px',
              color: '#666',
              fontSize: '12px'
            }}
          >
            {getRefreshMessage()}
          </Mui.Typography>
        </div>
      )}

      {/* Content */}
      <div 
        className="pull-content"
        style={{
          transform: pullToRefresh.isPulling ? 
            `translateY(${Math.min(pullToRefresh.pullDistance, 100)}px)` : 
            'translateY(0)',
          transition: pullToRefresh.isPulling ? 'none' : 'transform 0.3s ease'
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default PullToRefresh;